import { Box, Button, Grid, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { observer } from 'mobx-react';
import { SignatureField, SignatureFieldHandle } from '@roc/ui';
import { useStore } from '../../../hooks';

const useStyles = makeStyles(theme => ({
  container: {
    maxWidth: 1000,
    margin: 'auto',
    padding: theme.spacing(6),
    backgroundColor: '#fff',
    border: `1px solid ${theme.palette.grey[300]}`,
    borderRadius: theme.spacing(2),
    boxShadow: '0 4px 10px rgba(0,0,0,0.05)',
    overflow: 'auto',
    position: 'relative',
  },
  title: {
    fontWeight: 700,
    fontSize: '1.6rem',
    marginBottom: theme.spacing(4),
    textAlign: 'center',
  },
  titleLeft: {
    fontWeight: 700,
    fontSize: '1.6rem',
    marginBottom: theme.spacing(4),
    textAlign: 'left',
  },
  paragraph: {
    fontSize: '0.7rem',
    lineHeight: 1.7,
    marginBottom: theme.spacing(3),
    color: theme.palette.text.primary,
  },
  rightJustified: {
    fontSize: '0.7rem',
    lineHeight: 1.7,
    marginBottom: theme.spacing(3),
    color: theme.palette.text.primary,
    textAlign: 'right',
  },
  sectionHeader: {
    fontWeight: 600,
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(2),
    fontSize: '1rem',
  },
  submitButton: {
    color: '#fff',
    backgroundColor: theme.palette.primary.main,
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(4),
    width: '25%',
  },
}));

interface Props {
  signatureRef: React.RefObject<SignatureFieldHandle>;
  handleSubmit: any;
}

export const DocumentSigningContent = observer(({ signatureRef, handleSubmit }: Props) => {
  const classes = useStyles();
  const { wireAuthorizationStore } = useStore();
  const formValues = wireAuthorizationStore.getFormValues();

  return (
    <>
      <Box className={classes.container}>

        <Typography className={classes.paragraph}>
          LOAN NUMBER: {wireAuthorizationStore.loanId} <br></br>
          COLLATERAL ADDRESS: {wireAuthorizationStore.address} <br></br>
        </Typography>

        <Typography className={classes.paragraph}>
          Effective as of (Effective Date), on behalf of the undersigned entity, I hereby authorize the periodic wire transfer and deposit of funds in connection with above referenced Loan by Loan Funder LLC (Lender), or any of its parent companies, affiliates, authorized servicers, subservicers agents, representatives, or permitted successors or assigns (Lender Affiliates and Agents), into the following account:
        </Typography>

        <Typography className={classes.paragraph}>
          Account Name/Title: {formValues.accountName} <br></br>
          ABA (FedWire) Routing Number: {formValues.routingNumber} <br></br>
          Account Number: {formValues.accountNumber} <br></br>
          Bank Name: {formValues.bankName} <br></br>
          Entity Street Name: {formValues.streetName} <br></br>
          Entity City: {formValues.city} <br></br>
          Entity Zip Code: {formValues.zipCode} <br></br>
          Entity State: {formValues.state} <br></br>
        </Typography>

        <Typography className={classes.paragraph}>
          Below is only if For Further Credit Instructions are required. Please leave blank if not required for your bank wire
        </Typography>

        <Typography className={classes.paragraph}>
          FFC Account Name/Title: {formValues.fccAccountName} <br></br>
          FFC Routing Number: {formValues.fccRoutingNumber} <br></br>
          FFC Account Number: {formValues.fccAccountNumber} <br></br>
          FFC Bank Name: {formValues.fccBankName} <br></br>
          FFC Entity Street Name: {formValues.fccStreetName} <br></br>
          FFC Entity City: {formValues.fccCity} <br></br>
          FFC Entity Zip Code: {formValues.fccZipCode} <br></br>
          FFC Entity State: {formValues.fccState} <br></br>
        </Typography>

        <Typography className={classes.paragraph}>
          If the above account is not the undersigned entity’s account, or if the above account has an account name/title that is not the same as the name of the undersigned entity, on behalf of the undersigned entity, I hereby further expressly authorize the periodic wire transfer, and deposit of funds, in connection with the Loan into such account by Lender or any Lender Agent, and I hereby release Lender and all Lender Agents, and each of their respective officers, managers, directors, consultants, agents advisors, and representatives, from all loss, damage, liability, cost, or expense arising out of or resulting from the deposit of funds into such account.
        </Typography>

        <Typography className={classes.paragraph}>
          The foregoing authorization and wire instructions shall be standing and shall remain in full force and effect until such time as they are revoked or amended in writing by the undersigned. This Standing Authorization and Wire Instructions expressly revokes all prior Standing Authorizations and Wire Instructions (Construction Draws/Loan Advances) given on behalf of the undersigned entity in connection with the above referenced Loan. All such prior authorizations and wire instructions are void and of no further force or effect as of the above referenced Effective Date.
        </Typography>

        <Typography className={classes.paragraph}>
          On behalf of the undersigned entity, I hereby acknowledge, represent, and or warrant as follows:
        </Typography>

        <Typography className={classes.paragraph}>
          * I am a duly authorized representative of the undersigned entity and have been duly authorized by such entity to execute this Standing Authorization and Wire Instructions on behalf of such entity.<br></br>
          * The above wire and account information is accurate and correct and I acknowledge responsibility for any errors resulting from incorrect inaccurate information provided.<br></br>
          * I authorize Lender and any Lender Agent to effect wires/transfers deposits into the above specified account in connection with the Loan either (1) in connection with construction draws/advances made in relation to the Loan, or (2) otherwise in accordance with the terms and provisions of the loan documents related to the Loan.<br></br>
          * Lender reserves the right, on behalf of itself and any Lender Agent, to require additional verification of the above specified account information, whether written or oral, prior to authorizing a wire/transfer deposit, and such additional verification may delay the execution of a wire.<br></br>
          * The undersigned entity (1) releases Lender and all Lender Agents, and each of their respective officers, managers, directors, consultants agents, advisors, and representatives, from all loss, damage, liability cost, or expense (Loss) arising out of or resulting from any action taken by Lender or a Lender Agent upon instructions provided under this Standing Authorization and Wire Instructions, unless the Loss arose out of Lender’s or a Lender Agent’s failure to exercise ordinary care, act in good faith or act in accordance with the wire instructions given hereunder, and (2) agrees to indemnify and hold Lender and all Lender Agents harmless from any Loss arising out of or resulting from any action taken by Lender or a Lender Agent in good faith upon instructions provided under this Standing Authorization and Wire Instructions.<br></br>
          * Lender shall not be liable for refusing to execute a wire transfer instruction based on Lender’s reasonable belief that such wire is violative of applicable law or could cause Lender or a Lender Agent to incur legal liability for following such instructions.<br></br>
          * Lender cannot guarantee the length of time it takes for funds to be credited to the above specified account after a wire is initiated.<br></br>
          * Neither Lender nor any Lender Agent has any influence or responsibility for fees or surcharges imposed by other financial institutions involved in the transfer of funds<br></br>
        </Typography>

        <Typography className={classes.paragraph}>
          THIS STANDING AUTHORIZATION AND WIRE INSTRUCTIONS SHALL BE GOVERNED BY NEW YORK LAW.
        </Typography>

        <Typography className={classes.paragraph}>
          To accept and agree to the above wire authorization, you should sign below:
        </Typography>

        <SignatureField ref={signatureRef} penColor="green" />
      </Box>
      <Grid container style={{ justifyContent: 'center' }}>
        <Button
          variant="contained"
          className={classes.submitButton}
          onClick={handleSubmit}
        >
          SUBMIT
        </Button>
      </Grid>
    </>
  );
});